import { z } from "zod";

// -----------------------------------------------------------------------------
// Zod schemas — shared by frontend (react-hook-form) and Pages Functions
// -----------------------------------------------------------------------------

// AI extraction may return null for missing optional fields. Accept null and
// coerce to "" so downstream code never has to deal with null strings.
const optStr = (max: number) =>
  z.union([z.string().trim().max(max), z.null()])
    .transform((v) => v ?? "")
    .optional()
    .or(z.literal(""));

const optEmail = (max: number) =>
  z.union([z.string().trim().toLowerCase().email().max(max), z.null(), z.literal("")])
    .transform((v) => v ?? "")
    .optional();

const optUrl = (max: number) =>
  z.union([z.string().trim().url().max(max), z.null(), z.literal("")])
    .transform((v) => v ?? "")
    .optional();

export const ProspectSchema = z.object({
  first_name: z.string().trim().min(1, "First name required").max(80),
  last_name: z.string().trim().min(1, "Last name required").max(80),
  email: z.string().trim().toLowerCase().email("Valid email required").max(255),
  email_secondary: optEmail(255),
  title: optStr(120),
  phone: optStr(40),
  linkedin_url: optUrl(500),
});

export const CompanySchema = z.object({
  name: z.string().trim().min(1, "Company name required").max(200),
  domain: z.string().trim().toLowerCase().min(3, "Domain required").max(200)
    .regex(/^[a-z0-9.-]+\.[a-z]{2,}$/, "Valid domain required (e.g. acme.com)"),
  website: optUrl(500),
  industry: optStr(120),
});

export const MeetingSchema = z.object({
  date_local: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Valid date required (YYYY-MM-DD)"),
  time_local: z.string().regex(/^\d{2}:\d{2}$/, "Valid time required (HH:mm)"),
  timezone: z.string().min(3, "Timezone required"),
  duration_minutes: z.number().int().positive().max(480).optional(),
  meeting_link: optUrl(2000),
});

// AI extraction returns null for missing qualification fields; coerce to undefined.
const nullableString = (max: number) =>
  z.union([z.string().max(max), z.null()])
    .transform((v) => v ?? undefined)
    .optional();

export const QualificationSchema = z.object({
  current_solution: nullableString(500),
  budget: nullableString(200),
  authority: nullableString(300),
  needs_pains: nullableString(1000),
  timeframe: nullableString(200),
}).partial();

export const SubmissionContextSchema = z.object({
  handoff_notes: optStr(5000),
  source_campaign: optStr(120),
  qualification: QualificationSchema.optional(),
});

export const SubmissionSchema = z.object({
  bdr_id: z.string().min(1, "BDR is required"),
  idempotency_key: z.string().uuid(),
  prospect: ProspectSchema,
  company: CompanySchema,
  meeting: MeetingSchema,
  context: SubmissionContextSchema.optional(),
  attachments: z.object({
    pdf_r2_key: z.string().optional(),
    extraction_confidence: z.number().min(0).max(1).optional(),
  }).optional(),
  turnstile_token: z.string().optional(),
});

// Server-side additional guard: meeting must be today or future, max 90 days out.
// (Pure date math — does not handle timezone conversion, which happens before this.)
export function validateMeetingDate(dateLocal: string): string | null {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateLocal + "T00:00:00");
  const max = new Date(today);
  max.setDate(max.getDate() + 90);
  if (target < today) return "Meeting date cannot be in the past";
  if (target > max) return "Meeting date cannot be more than 90 days out";
  return null;
}

export type SubmissionInput = z.infer<typeof SubmissionSchema>;
